import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEntity } from '../../context/EntityContext';
import api, { propertyAPI, unitAPI, bookingFormAPI, developerTemplateAPI, bookingTemplateAPI } from '../../lib/api';
import { formatCurrency, cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { toast } from 'sonner';
import {
  Building2, Home, FileText, CreditCard, CheckCircle,
  ArrowLeft, ArrowRight, Loader2, Wand2, AlertCircle,
  Download, Eye, ChevronLeft, ChevronRight, Upload, Receipt, ImageIcon,
  Users2, Check, ChevronsUpDown, X as XIcon,
} from 'lucide-react';
import SignaturePad from '../../components/common/SignaturePad';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateBooking = () => {
  const { user } = useAuth();
  const { currentEntity } = useEntity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const preSelectedPropertyId = searchParams.get('property_id');
  const preSelectedUnitId = searchParams.get('unit_id');
  // V4.19 — appointment linkage: passed from Appointments.jsx when the agent
  // clicks "Proceed to Booking". Included in the payload so the backend can
  // (a) resolve the customer, and (b) transition the appointment status to
  // `booking_initiated` only after the booking is actually persisted.
  const preSelectedAppointmentId = searchParams.get('appointment_id');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingEntity, setBookingEntity] = useState(currentEntity !== 'all' ? currentEntity : 'sam_rumah');

  // Data states
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [autoFillData, setAutoFillData] = useState({});

  // Developer template states
  const [devTemplate, setDevTemplate] = useState(null);
  const [devTemplateFields, setDevTemplateFields] = useState(null);
  // V4.25.23 — set when a Booking Form Template is linked to the property but
  // has no fields defined yet (so Step 2 can show a precise "add fields" note).
  const [bookingTemplateEmpty, setBookingTemplateEmpty] = useState(false);
  const [formData, setFormData] = useState({});

  // Payment state
  const [paymentInfo, setPaymentInfo] = useState({
    booking_fee_amount: '',
    payment_method: 'bank_transfer',
    bank_reference: '',
  });
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [signatures, setSignatures] = useState({});

  // Commission Engine V2 — co-broke split (up to 4 agents, manual %). Primary
  // is the logged-in agent. Empty/disabled => single-agent booking (100%).
  const [coBrokeEnabled, setCoBrokeEnabled] = useState(false);
  const [coBrokeRows, setCoBrokeRows] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [coBrokePicker, setCoBrokePicker] = useState(null);

  useEffect(() => {
    if (coBrokeEnabled && agentsList.length === 0) {
      api.get('/agents/directory', { params: { limit: 3000 } })
        .then((res) => setAgentsList(res.data || []))
        .catch(() => {});
    }
    if (coBrokeEnabled && coBrokeRows.length === 0 && user?.id) {
      setCoBrokeRows([{ agent_id: user.id, allocation_percent: 100, primary: true }]);
    }
  }, [coBrokeEnabled, user]);

  const coBrokeTotal = coBrokeRows.reduce((s, r) => s + (parseFloat(r.allocation_percent) || 0), 0);
  const agentLabel = (id) => {
    if (id === user?.id) return `${user?.agent_code || user?.user_code || 'You'} · ${user?.full_name || 'You'} (you)`;
    const a = agentsList.find((x) => x.id === id);
    return a ? `${a.agent_code || a.user_code || ''} · ${a.full_name}` : 'Select agent';
  };
  const addCoBrokeRow = () => { if (coBrokeRows.length < 4) setCoBrokeRows([...coBrokeRows, { agent_id: '', allocation_percent: 0 }]); };
  const removeCoBrokeRow = (i) => setCoBrokeRows(coBrokeRows.filter((_, idx) => idx !== i));
  const setCoBrokeRow = (i, patch) => setCoBrokeRows(coBrokeRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Loan v3.5 Phase B-1 — Loan Profiling section (passed through to form_data so
  // backend auto-create can pick them up). Phase C will use these to call Aicon.
  const [loanInfo, setLoanInfo] = useState({
    requested_loan_amount: '',
    monthly_income: '',
    employment_type: '',           // salaried / self_employed / commission
    tenure_pref_years: '30',
    existing_loans: [],             // [{lender, monthly_installment, outstanding}]
    nationality: 'MY',
    pdpa_consent: false,
  });
  const [showLoanSection, setShowLoanSection] = useState(true);

  // SAM Rumah customer linkage (Feature 5 — V4.5)
  // When entity = sam_rumah, agent can pick an in-app customer who becomes the
  // booking's customer_id (and later the Level-0 entry in the SAM Rumah claim).
  const [samCustomer, setSamCustomer] = useState(null); // { id, full_name, email, phone, user_code }
  const [samCustomerSearch, setSamCustomerSearch] = useState('');
  const [samCustomerOptions, setSamCustomerOptions] = useState([]);
  const [samCustomerSearching, setSamCustomerSearching] = useState(false);

  // Search customers as the agent types (debounced)
  useEffect(() => {
    if (bookingEntity !== 'sam_rumah') return;
    const term = samCustomerSearch.trim();
    if (term.length < 2) { setSamCustomerOptions([]); return; }
    let cancelled = false;
    setSamCustomerSearching(true);
    const t = setTimeout(() => {
      api.get('/users', { params: { role: 'customer', search: term, limit: 10 } })
        .then(res => {
          if (cancelled) return;
          const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
          setSamCustomerOptions(items);
        })
        .catch((err) => {
          if (cancelled) return;
          setSamCustomerOptions([]);
          const msg = err?.response?.data?.detail || 'Customer search failed';
          // eslint-disable-next-line no-console
          console.warn('[sam-rumah-customer-search]', msg);
        })
        .finally(() => { if (!cancelled) setSamCustomerSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [samCustomerSearch, bookingEntity]);

  // Reset selected customer if entity flips away from SAM Rumah
  useEffect(() => {
    if (bookingEntity !== 'sam_rumah') {
      setSamCustomer(null);
      setSamCustomerSearch('');
      setSamCustomerOptions([]);
    }
  }, [bookingEntity]);

  // Preview overlay states
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Form state
  const [selectedPropertyId, setSelectedPropertyId] = useState(preSelectedPropertyId || '');
  const [propertyPickerOpen, setPropertyPickerOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(preSelectedUnitId || '');
  const [manualUnitNumber, setManualUnitNumber] = useState('');

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const isExclusive = selectedProperty?.is_exclusive !== false;
  const hasDevTemplate = !!devTemplateFields && devTemplateFields.total_fields > 0;

  // Steps: Property & Unit -> Developer Form -> Payment Details -> Confirmation
  const totalSteps = hasDevTemplate ? 4 : 3;
  const paymentStep = hasDevTemplate ? 3 : 2;

  useEffect(() => { loadProperties(); }, []);

  useEffect(() => {
    if (selectedPropertyId) loadPropertyData(selectedPropertyId);
  }, [selectedPropertyId]);

  useEffect(() => {
    if (selectedUnitId && autoFillData.property_id) loadUnitAutoFill();
  }, [selectedUnitId]);

  const loadProperties = async () => {
    try {
      const res = await propertyAPI.getProperties();
      // V4.18.43 — Hide zero-availability properties from the picker.
      // Backend enriches each property with `available_units_count` on
      // GET /properties. Nullable fallback keeps the picker populated
      // for legacy responses that don't emit the field.
      // V4.25.6 — ALWAYS keep the property this booking is for (the
      // pre-selected/appointment property), even if its availability count
      // is 0, otherwise it auto-fills the details but is missing from the
      // dropdown and cannot be (re)selected.
      const all = res.data || [];
      const preId = preSelectedPropertyId;
      const list = all.filter(p =>
        p.available_units_count == null || Number(p.available_units_count) > 0 || p.id === preId
      );
      setProperties(list);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const loadPropertyData = async (propertyId) => {
    setLoading(true);
    try {
      const unitsRes = await unitAPI.getUnits({ property_id: propertyId, status: 'available' });
      setUnits(unitsRes.data || []);

      // Load auto-fill data
      try {
        const templateRes = await propertyAPI.getBookingTemplate(propertyId);
        setAutoFillData(templateRes.data.auto_fill_data);
      } catch (e) {
        console.error('Error loading auto-fill data:', e);
      }

      // Check if property has a developer template
      const propertyRes = await propertyAPI.getProperty(propertyId);
      const property = propertyRes.data;
      // Auto-populate booking fee from property
      if (property?.total_booking_fee) {
        setPaymentInfo(prev => ({ ...prev, booking_fee_amount: property.total_booking_fee.toString() }));
      }

      // Resolve the form for Step 2 using WHICHEVER template the property has
      // linked. Precedence: Developer PDF Template (link #2) first, then the
      // Booking Form Template (link #1) as fallback.
      setBookingTemplateEmpty(false);
      let resolved = false;

      // ---- Priority 1: Developer PDF Template ----
      if (property?.developer_template_id) {
        try {
          const dtRes = await developerTemplateAPI.getTemplate(property.developer_template_id);
          if (dtRes.data.field_count > 0 || (dtRes.data.fields && dtRes.data.fields.length > 0)) {
            const fieldsRes = await developerTemplateAPI.getFieldsForBooking(property.developer_template_id);
            setDevTemplate({ ...dtRes.data, _source: 'developer' });
            setDevTemplateFields({ ...fieldsRes.data, _source: 'developer' });

            const initialData = {};
            for (const section of (fieldsRes.data.sections || [])) {
              for (const field of (section.fields || [])) {
                initialData[field.id] = '';
              }
            }
            setFormData(initialData);
            resolved = true;
          }
        } catch (e) {
          console.error('Error loading developer template:', e);
        }
      }

      // ---- Priority 2: Booking Form Template (fallback) ----
      if (!resolved && property?.booking_template_id) {
        try {
          const btRes = await bookingTemplateAPI.getTemplate(property.booking_template_id);
          const bt = btRes.data || {};
          const formFields = bt.form_fields || [];
          if (formFields.length > 0) {
            const normOptions = (o) => Array.isArray(o)
              ? o
              : (o ? String(o).split(',').map(s => s.trim()).filter(Boolean) : []);
            const sections = [{
              name: bt.name || 'Booking Form',
              fields: formFields.map((f) => ({
                id: f.name,
                label: f.label || f.name,
                field_type: f.field_type || 'text',
                options: normOptions(f.options),
                required: !!f.required,
                description: f.description || '',
              })),
            }];
            setDevTemplate({
              id: bt.id,
              name: bt.name,
              developer_name: bt.developer_name || bt.developer || '',
              project_name: bt.project_name || '',
              _source: 'booking_form',
            });
            setDevTemplateFields({ total_fields: formFields.length, sections, _source: 'booking_form' });

            const initialData = {};
            formFields.forEach((f) => { initialData[f.name] = ''; });
            setFormData(initialData);
            resolved = true;
          } else {
            // Linked but no fields defined yet.
            setDevTemplate(null);
            setDevTemplateFields(null);
            setBookingTemplateEmpty(true);
          }
        } catch (e) {
          console.error('Error loading booking form template:', e);
        }
      }

      if (!resolved) {
        setDevTemplate(null);
        setDevTemplateFields(null);
      }
    } catch (error) {
      console.error('Error loading property data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnitAutoFill = async () => {
    try {
      const templateRes = await propertyAPI.getBookingTemplate(selectedPropertyId, selectedUnitId);
      setAutoFillData(templateRes.data.auto_fill_data);
    } catch (error) {
      console.error('Error loading unit auto-fill:', error);
    }
  };

  const selectedUnit = units.find(u => u.id === selectedUnitId);

  const handlePaymentProofChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB');
      return;
    }
    setPaymentProofFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPaymentProofPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // Robust "is this field filled?" check — handles strings, arrays (checkboxes),
  // booleans (single checkbox), numbers, dates, and objects.
  const isFieldFilled = (val, fieldType = null) => {
    if (val === undefined || val === null) return false;
    // For a required checkbox/consent, the user must check it (value === true / 'true')
    if (fieldType === 'checkbox') {
      return val === true || val === 'true';
    }
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'boolean') return val === true;
    if (typeof val === 'number') return !Number.isNaN(val);
    if (typeof val === 'object') return Object.keys(val).length > 0;
    return String(val).trim().length > 0;
  };

  // Returns list of required-but-unfilled field labels (empty = OK to proceed).
  const missingRequiredFields = (stepNum) => {
    if (stepNum === 2 && hasDevTemplate) {
      const missing = [];
      for (const section of (devTemplateFields?.sections || [])) {
        for (const field of (section.fields || [])) {
          if (field.required && !isFieldFilled(formData[field.id], field.field_type)) {
            missing.push(field.label || field.id);
          }
        }
      }
      return missing;
    }
    return [];
  };

  // V4.18.26 — inline booking-fee ceiling validation.
  // Computed on every render so the error appears as the user types, not
  // just after they hit "Next". Backend enforces the same rule server-side.
  const bookingFeeError = (() => {
    const totalCeiling = Number(selectedProperty?.total_booking_fee || 0);
    const entered = parseFloat(paymentInfo.booking_fee_amount);
    if (Number.isNaN(entered) || entered <= 0) return null;   // "must be > 0" is handled by canProceed
    if (totalCeiling > 0 && entered > totalCeiling) {
      return `Booking fee cannot exceed the total booking fee (RM ${totalCeiling.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).`;
    }
    return null;
  })();

  const canProceed = (stepNum) => {
    if (stepNum === 1) {
      if (!selectedPropertyId) return false;
      if (isExclusive) return !!selectedUnitId;
      return !!manualUnitNumber.trim();
    }
    if (stepNum === 2 && hasDevTemplate) {
      return missingRequiredFields(stepNum).length === 0;
    }
    if (stepNum === paymentStep) {
      // V4.18.43 — Booking fee is REQUIRED only when the selected property
      // has a configured `total_booking_fee > 0`. Fee-less properties
      // (reservation-only campaigns, etc.) allow booking_fee_amount = 0.
      const feeIsConfigured = Number(selectedProperty?.total_booking_fee || 0) > 0;
      if (feeIsConfigured) {
        if (!paymentInfo.booking_fee_amount || parseFloat(paymentInfo.booking_fee_amount) <= 0) return false;
        if (bookingFeeError) return false;
      }
      if (!paymentInfo.payment_method) return false;
      if (!paymentInfo.bank_reference.trim()) return false;
      if (!paymentProofFile && !paymentProofPreview) return false;
      return true;
    }
    return true;
  };

  const validateStep = (stepNum) => {
    if (stepNum === paymentStep && !paymentProofFile && !paymentProofPreview) {
      toast.error('Payment proof upload is mandatory');
      return false;
    }
    return canProceed(stepNum);
  };

  const handleNext = () => {
    if (!validateStep(step)) {
      if (!canProceed(step)) {
        const missing = missingRequiredFields(step);
        if (missing.length > 0) {
          const preview = missing.slice(0, 3).join(', ');
          const more = missing.length > 3 ? ` (+${missing.length - 3} more)` : '';
          toast.error(`Missing required field(s): ${preview}${more}`);
        } else {
          toast.error('Please fill in all required fields');
        }
      }
      return;
    }
    setStep(step + 1);
  };

  const handlePreviewOverlay = async (page = 0) => {
    if (!devTemplate?.id) {
      toast.error('Template not loaded. Please re-select the property.');
      return;
    }
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      if (!devTemplate.positions_detected) {
        toast.info('Detecting field positions...');
        try {
          await developerTemplateAPI.detectPositions(devTemplate.id);
          const refreshed = await developerTemplateAPI.getTemplate(devTemplate.id);
          setDevTemplate(refreshed.data);
        } catch (detectErr) {
          console.warn('Position detection skipped (may require admin):', detectErr);
        }
      }
      const res = await developerTemplateAPI.previewOverlay(devTemplate.id, { form_values: formData, page });
      setPreviewImage(res.data.image);
      setPreviewPage(page);
    } catch (error) {
      console.error('Preview overlay error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
      setShowPreview(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadFilledPdf = async (bookingId = null) => {
    if (!devTemplate?.id) {
      toast.error('Template not loaded. Please re-select the property.');
      return;
    }
    setGeneratingPdf(true);
    try {
      if (!devTemplate.positions_detected) {
        try {
          await developerTemplateAPI.detectPositions(devTemplate.id);
        } catch (detectErr) {
          console.warn('Position detection skipped:', detectErr);
        }
      }
      const res = await developerTemplateAPI.generateFilled(devTemplate.id, { form_values: formData, booking_id: bookingId, signatures });
      const pdfUrl = `${BACKEND_URL}/api${res.data.pdf_url}`;
      window.open(pdfUrl, '_blank');
      toast.success(`PDF generated with ${res.data.filled_count} fields filled`);
    } catch (error) {
      console.error('Generate PDF error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Build form_data from dynamic fields
      const fd = { ...formData };
      for (const section of (devTemplateFields?.sections || [])) {
        for (const field of (section.fields || [])) {
          const v = formData[field.id];
          if (!v) continue;
          if (field.data_mapping === 'form_data.buyer_name') fd.buyer_name = v;
          if (field.data_mapping === 'form_data.buyer_ic') fd.buyer_ic = v;
          if (field.data_mapping === 'form_data.buyer_phone') fd.buyer_phone = v;
          if (field.data_mapping === 'form_data.buyer_email') fd.buyer_email = v;
          if (field.data_mapping === 'form_data.buyer_address') fd.buyer_address = v;
        }
      }
      // V4.24.18 — Removed placeholder defaults (`buyer_name = 'Customer'`,
      // `buyer_ic = '-'`) that were leaking into the Loan Questionnaire.
      // Instead, if the template didn't map buyer_ic explicitly, scan ALL
      // typed values for an NRIC-shaped pattern so the customer's typed
      // NRIC still lands on the top-level field.
      const icRe = /^\d{6}-?\d{2}-?\d{4}$/;
      if (!fd.buyer_ic || fd.buyer_ic === '-') {
        for (const v of Object.values(formData)) {
          if (typeof v === 'string') {
            const clean = v.replace(/\s+/g, '');
            if (icRe.test(clean)) { fd.buyer_ic = clean; break; }
          }
        }
        if (fd.buyer_ic === '-') delete fd.buyer_ic;
      }
      // Same idea for buyer_name — if the template didn't map it, pick a real
      // NAME field. V4.25.17: previously a broad /purchaser/ regex also matched
      // non-name keys like `purchaser_1_tax_id` (e.g. "P00001"), leaking a code
      // into the buyer name. Now we (1) try an ordered list of known name
      // aliases, then (2) fall back only to keys that actually contain "name"
      // (excluding spouse/maiden/mother/company/guarantor/etc.).
      if (!fd.buyer_name || fd.buyer_name === 'Customer') {
        const preferredNameKeys = [
          'buyer_name', 'customer_name', 'full_name', 'name',
          'purchaser_1_name', 'purchaser_name_1', 'purchaser_name',
          'name_1', 'buyer_1_name', 'applicant_name', 'name_of_intending_purchaser_1',
        ];
        const otherNameKeys = Object.keys(formData).filter((k) =>
          /name/i.test(k)
          && !preferredNameKeys.includes(k)
          && !/(spouse|maiden|mother|company|bank|beneficiary|guarantor|next_of_kin|witness|agent)/i.test(k)
        );
        for (const k of [...preferredNameKeys, ...otherNameKeys]) {
          const v = formData[k];
          if (typeof v === 'string' && v.trim() && v.trim() !== 'Customer') { fd.buyer_name = v.trim(); break; }
        }
        if (fd.buyer_name === 'Customer') delete fd.buyer_name;
      }

      // Loan v3.5 profiling fields — passed through form_data; backend's
      // create_loan_application_for_booking() reads them to seed the case.
      if (loanInfo.requested_loan_amount) fd.loan_amount = parseFloat(loanInfo.requested_loan_amount) || 0;
      if (loanInfo.monthly_income) fd.monthly_income = parseFloat(loanInfo.monthly_income) || 0;
      if (loanInfo.employment_type) fd.employment_type = loanInfo.employment_type;
      if (loanInfo.tenure_pref_years) fd.tenure_pref_years = parseInt(loanInfo.tenure_pref_years) || 30;
      if (loanInfo.existing_loans?.length) fd.existing_loans = loanInfo.existing_loans;
      if (loanInfo.nationality) fd.nationality = loanInfo.nationality;
      if (loanInfo.pdpa_consent) {
        fd.pdpa_consent = true;
        fd.pdpa_consent_at = new Date().toISOString();
      }

      // Commission Engine V2 — attach validated co-broke split (if enabled).
      let coBrokePayload = null;
      if (coBrokeEnabled) {
        if (coBrokeRows.some((r) => !r.agent_id)) {
          toast.error('Pick an agent for every co-broke row'); setSubmitting(false); return;
        }
        const ids = coBrokeRows.map((r) => r.agent_id);
        if (new Set(ids).size !== ids.length) {
          toast.error('The same agent cannot be selected more than once'); setSubmitting(false); return;
        }
        if (Math.abs(coBrokeTotal - 100) > 0.01) {
          toast.error(`Co-broke allocations must total 100% (now ${coBrokeTotal}%)`); setSubmitting(false); return;
        }
        coBrokePayload = coBrokeRows.map((r) => ({ agent_id: r.agent_id, allocation_percent: parseFloat(r.allocation_percent) }));
      }

      const bookingData = {
        property_id: selectedPropertyId,
        unit_id: isExclusive ? selectedUnitId : null,
        manual_unit_number: !isExclusive ? manualUnitNumber : null,
        developer_template_id: devTemplate?._source === 'developer' ? devTemplate.id : null,
        booking_template_id: devTemplate?._source === 'booking_form' ? devTemplate.id : null,
        entity: bookingEntity,
        // V4.19 — link this booking to the originating appointment so the
        // backend can flip its status to `booking_initiated` on success.
        appointment_id: preSelectedAppointmentId || null,
        // Feature 5 (V4.5): for SAM Rumah, attach the in-app customer the agent picked.
        // Backend resolves this to booking.customer_id and later to claim Level-0 recipient.
        customer_id: bookingEntity === 'sam_rumah' && samCustomer ? samCustomer.id : null,
        sam_rumah_customer_id: bookingEntity === 'sam_rumah' && samCustomer ? samCustomer.id : null,
        form_data: fd,
        signatures: signatures,
        // Payment fields
        booking_fee_amount: parseFloat(paymentInfo.booking_fee_amount) || 0,
        payment_method: paymentInfo.payment_method,
        bank_reference: paymentInfo.bank_reference,
        payment_proof: paymentProofPreview, // base64
        co_broke_participants: coBrokePayload,
      };

      await bookingFormAPI.createWithForm(bookingData);
      toast.success('Booking created successfully! Payment proof submitted for verification.');
      navigate('/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.id] ?? '';
    const onChange = (val) => setFormData(prev => ({ ...prev, [field.id]: val }));
    const inputClass = "bg-gray-50 border-gray-100 text-gray-900 mt-1";

    switch (field.field_type) {
      case 'date':
        return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} data-testid={`field-${field.id}`} />;
      case 'number':
        return <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} data-testid={`field-${field.id}`} />;
      case 'email':
        return <Input type="email" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder="email@example.com" data-testid={`field-${field.id}`} />;
      case 'phone':
        return <Input type="tel" value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder="+60 12-345 6789" data-testid={`field-${field.id}`} />;
      case 'address':
        return <Textarea value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} min-h-[80px]`} placeholder="Enter full address" data-testid={`field-${field.id}`} />;
      case 'dropdown':
      case 'select':
      case 'radio': {
        const opts = field.options && field.options.length > 0 ? field.options : [];
        if (opts.length === 0) {
          // No options — fall back to a free text input so user can still fill
          return <Input value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder={field.description || `Enter ${(field.label || '').toLowerCase()}`} data-testid={`field-${field.id}`} />;
        }
        return (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className={inputClass} data-testid={`field-${field.id}`}>
              <SelectValue placeholder={`Select ${(field.label || '').toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {opts.map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case 'checkbox':
        return (
          <div className="flex items-center gap-2 mt-2">
            <Checkbox
              checked={value === 'true' || value === true}
              onCheckedChange={(c) => onChange(c ? 'true' : 'false')}
              data-testid={`field-${field.id}`}
            />
            <span className="text-sm text-gray-500">{field.description || 'Check if applicable'}</span>
          </div>
        );
      case 'signature':
        return (
          <SignaturePad
            label={field.label || 'Signature'}
            onSignature={(data) => {
              setSignatures(prev => ({ ...prev, [field.id]: data }));
              // Also mirror to the generic 'buyer' / 'agent' keys so downstream
              // PDF overlay + booking payload stay in sync.
              const lbl = (field.label || '').toLowerCase();
              if (/purchaser|buyer|customer/.test(lbl)) {
                setSignatures(prev => ({ ...prev, buyer: data }));
              } else if (/agent|witness|attended|sales/.test(lbl)) {
                setSignatures(prev => ({ ...prev, agent: data }));
              }
              onChange(data ? 'signed' : '');
            }}
            existingSignature={signatures[field.id]}
          />
        );
      default:
        return <Input value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder={field.description || `Enter ${(field.label || '').toLowerCase()}`} data-testid={`field-${field.id}`} />;
    }
  };

  const getSteps = () => {
    if (hasDevTemplate) {
      return [
        { num: 1, label: 'Property & Unit', icon: Building2 },
        { num: 2, label: 'Developer Form', icon: FileText },
        { num: 3, label: 'Payment Details', icon: Receipt },
        { num: 4, label: 'Confirmation', icon: CheckCircle },
      ];
    }
    return [
      { num: 1, label: 'Property & Unit', icon: Building2 },
      { num: 2, label: 'Payment Details', icon: Receipt },
      { num: 3, label: 'Confirmation', icon: CheckCircle },
    ];
  };

  const isConfirmStep = step === totalSteps;

  if (loading && properties.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#4E50FF]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-booking-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/bookings')} className="text-gray-500" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Booking</h1>
          <p className="text-gray-500">Complete the form to create a property booking</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg p-4">
        {getSteps().map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[#4E50FF]' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step > s.num ? 'bg-[#4E50FF] text-gray-900' :
                step === s.num ? 'bg-[#4E50FF]/10 border-2 border-[#4E50FF] text-[#4E50FF]' :
                'bg-gray-100 text-gray-400'
              }`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className="hidden md:block text-sm font-medium">{s.label}</span>
            </div>
            {idx < getSteps().length - 1 && (
              <div className={`w-12 lg:w-24 h-0.5 mx-2 ${step > s.num ? 'bg-[#4E50FF]' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Dev Template Banner */}
      {hasDevTemplate && selectedPropertyId && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-400">
            <Wand2 className="w-5 h-5" />
            <span className="font-medium">
              {devTemplate?._source === 'booking_form' ? 'Booking Form' : 'Developer Form'}: {devTemplate?.name}
            </span>
            <Badge className="bg-purple-100 text-purple-700 text-xs">{devTemplateFields?.total_fields} fields</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {devTemplate?._source === 'booking_form'
              ? `Fields from the linked booking form template "${devTemplate?.name}" will be used.`
              : `Fields from ${devTemplate?.developer_name}'s booking form will be used (${devTemplate?.project_name})`}
          </p>
        </div>
      )}

      {/* Booking Form Template linked but has no fields yet */}
      {!hasDevTemplate && bookingTemplateEmpty && selectedPropertyId && !loading && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4" data-testid="booking-form-empty-notice">
          <div className="flex items-center gap-2 text-amber-400">
            <FileText className="w-5 h-5" />
            <span className="font-medium">Booking Form Has No Fields</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            A Booking Form Template is linked to this property but no fields have been added to it yet.
            Please add fields to that template in <span className="font-medium">Admin &gt; Booking Templates</span> before creating a booking.
          </p>
        </div>
      )}

      {/* No template linked at all */}
      {!hasDevTemplate && !bookingTemplateEmpty && selectedPropertyId && !loading && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4" data-testid="no-form-notice">
          <div className="flex items-center gap-2 text-amber-400">
            <FileText className="w-5 h-5" />
            <span className="font-medium">No Booking Form Linked</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            This property has no booking form linked. Link either a <span className="font-medium">Booking Form Template</span> (Admin &gt; Booking Templates)
            or a <span className="font-medium">Developer PDF Template</span> (Admin &gt; Developer PDFs) on the property's edit page before creating a booking.
          </p>
        </div>
      )}

      <Card className="border-gray-100">
        <CardContent className="pt-6">
          {/* Step 1: Property & Unit Selection */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Entity Selection */}
              <div>
                <Label className="text-gray-700 text-base">Booking Entity *</Label>
                <p className="text-sm text-gray-400 mb-2">Select which entity this booking is under</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBookingEntity('sam_rumah')}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      bookingEntity === 'sam_rumah'
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-100'
                    }`}
                    data-testid="booking-entity-sr"
                  >
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center font-bold text-sm ${
                      bookingEntity === 'sam_rumah' ? 'bg-emerald-500 text-gray-900' : 'bg-gray-100 text-gray-500'
                    }`}>SR</div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${bookingEntity === 'sam_rumah' ? 'text-[#4E50FF]' : 'text-gray-700'}`}>SAM Rumah</p>
                      <p className="text-[10px] text-gray-400">Customer cashback</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setBookingEntity('sp_sam')}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      bookingEntity === 'sp_sam'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-100'
                    }`}
                    data-testid="booking-entity-sp"
                  >
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center font-bold text-sm ${
                      bookingEntity === 'sp_sam' ? 'bg-blue-500 text-gray-900' : 'bg-gray-100 text-gray-500'
                    }`}>SP</div>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${bookingEntity === 'sp_sam' ? 'text-blue-400' : 'text-gray-700'}`}>SP SAM Property</p>
                      <p className="text-[10px] text-gray-400">Direct commission</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* SAM Rumah Customer Lookup (Feature 5 — V4.5) */}
              {bookingEntity === 'sam_rumah' && (
                <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-4" data-testid="sam-rumah-customer-panel">
                  <Label className="text-gray-700 text-base">SAM Rumah Customer (In-App Buyer)</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Optional: link this booking to an existing in-app customer. They will appear automatically as the Level-0 recipient on the SAM Rumah commission claim form.
                    Search by name, email, phone, or user code (e.g. <span className="font-mono">C-A8F2D3</span>).
                  </p>
                  {samCustomer ? (
                    <div className="flex items-center justify-between bg-white border border-emerald-300 rounded-md px-3 py-2" data-testid="sam-rumah-customer-selected">
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold">
                          {(samCustomer.full_name || 'C').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{samCustomer.full_name}</p>
                          <p className="text-xs text-gray-500">
                            {samCustomer.user_code && <span className="font-mono text-indigo-600 mr-2">{samCustomer.user_code}</span>}
                            {samCustomer.phone || samCustomer.email}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setSamCustomer(null); setSamCustomerSearch(''); }}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                        data-testid="sam-rumah-customer-clear"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={samCustomerSearch}
                        onChange={(e) => setSamCustomerSearch(e.target.value)}
                        placeholder="Type at least 2 characters..."
                        className="w-full bg-white border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                        data-testid="sam-rumah-customer-search"
                      />
                      {samCustomerSearch.length >= 2 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto z-20">
                          {samCustomerSearching && (
                            <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                          )}
                          {!samCustomerSearching && samCustomerOptions.length === 0 && (
                            <div className="px-3 py-2 text-xs text-gray-500">No matching customers</div>
                          )}
                          {samCustomerOptions.map(u => (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => { setSamCustomer(u); setSamCustomerOptions([]); }}
                              className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-100 last:border-b-0"
                              data-testid={`sam-rumah-customer-option-${u.id}`}
                            >
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-gray-800">{u.full_name}</span>
                                {u.user_code && <span className="font-mono text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{u.user_code}</span>}
                              </div>
                              <div className="text-[11px] text-gray-500">{u.phone || ''} {u.email && `• ${u.email}`}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Separator className="bg-gray-100" />

              <div>
                <Label className="text-gray-700 text-base">Select Property *</Label>
                <p className="text-sm text-gray-400 mb-2">Choose the property for this booking</p>
                <Popover open={propertyPickerOpen} onOpenChange={setPropertyPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={propertyPickerOpen}
                      className="w-full justify-between bg-gray-50 border-gray-100 text-gray-900 font-normal hover:bg-gray-100"
                      data-testid="property-select"
                    >
                      {selectedProperty ? (
                        <span className="flex items-center gap-2 truncate">
                          <Building2 className="w-4 h-4 text-gray-500 shrink-0" />
                          <span className="truncate">{selectedProperty.name}</span>
                          {selectedProperty.location && <span className="text-gray-400 truncate">- {selectedProperty.location}</span>}
                        </span>
                      ) : (
                        <span className="text-gray-400">Select a property</span>
                      )}
                      <ChevronsUpDown className="w-4 h-4 text-gray-400 shrink-0 opacity-60" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 bg-white border-gray-200" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                    <Command
                      filter={(value, search) => {
                        const p = properties.find(x => x.id === value);
                        if (!p) return 0;
                        const hay = `${p.name || ''} ${p.location || ''} ${p.developer || ''}`.toLowerCase();
                        return hay.includes(search.toLowerCase().trim()) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder="Search property by name or location..." data-testid="property-search-input" />
                      <CommandList>
                        <CommandEmpty>No property found.</CommandEmpty>
                        <CommandGroup>
                          {properties.map((property) => (
                            <CommandItem
                              key={property.id}
                              value={property.id}
                              onSelect={(val) => {
                                setSelectedPropertyId(val);
                                setPropertyPickerOpen(false);
                              }}
                              data-testid={`property-option-${property.id}`}
                            >
                              <Check className={cn('mr-2 h-4 w-4', selectedPropertyId === property.id ? 'opacity-100 text-[#4E50FF]' : 'opacity-0')} />
                              <Building2 className="w-4 h-4 text-gray-500 mr-2 shrink-0" />
                              <span>{property.name}</span>
                              {property.location && <span className="text-gray-400 ml-1">- {property.location}</span>}
                              {property.developer_template_id && <Badge className="text-[10px] bg-purple-50 text-purple-600 ml-2">Dev Form</Badge>}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {selectedPropertyId && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#4E50FF]" />
                      Property Details (Auto-filled)
                      <Badge className={`ml-auto text-xs ${isExclusive ? 'bg-[#4E50FF]/20 text-[#4E50FF] border border-[#4E50FF]/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>
                        {isExclusive ? 'Exclusive' : 'Non-Exclusive'}
                      </Badge>
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><span className="text-gray-400">Name:</span> <span className="text-gray-900 ml-2">{autoFillData.property_name || selectedProperty?.name}</span></div>
                      <div><span className="text-gray-400">Location:</span> <span className="text-gray-900 ml-2">{autoFillData.property_location || selectedProperty?.location}</span></div>
                      <div><span className="text-gray-400">Developer:</span> <span className="text-gray-900 ml-2">{autoFillData.property_developer || selectedProperty?.developer}</span></div>
                      <div><span className="text-gray-400">Type:</span> <span className="text-gray-900 ml-2 capitalize">{autoFillData.property_type || selectedProperty?.property_type}</span></div>
                    </div>
                  </div>

                  <Separator className="bg-gray-100" />

                  {isExclusive ? (
                    <div>
                      <Label className="text-gray-700 text-base">Select Unit *</Label>
                      <p className="text-sm text-gray-400 mb-2">
                        {loading ? 'Loading units...' : (units.length > 0 ? `${units.length} available units` : 'No available units')}
                      </p>
                      {!loading && units.length === 0 ? (
                        <div
                          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
                          data-testid="no-units-empty-state"
                        >
                          <p className="font-medium mb-1">This exclusive project has no bookable units yet.</p>
                          <p className="text-amber-700">
                            The "Total Units" number on the property is only a summary — bookable units must be added as
                            inventory. Ask your admin/PIC to add units under <span className="font-medium">Properties → Unit Availability</span>{' '}
                            (Add Unit or Bulk Generate), or switch this project to <span className="font-medium">Non-Exclusive</span> so the unit number can be entered manually.
                          </p>
                        </div>
                      ) : (
                        <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
                          <SelectTrigger className="bg-gray-50 border-gray-100 text-gray-900" data-testid="unit-select">
                            <SelectValue placeholder="Select a unit" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 max-h-60">
                            {units.map((unit) => (
                              <SelectItem key={unit.id} value={unit.id}>
                                <div className="flex items-center gap-2">
                                  <Home className="w-4 h-4 text-gray-500" />
                                  <span>{unit.unit_number}</span>
                                  {unit.unit_type && <Badge variant="outline" className="text-xs">{unit.unit_type}</Badge>}
                                  <span className="text-[#4E50FF] font-mono ml-4">{formatCurrency(unit.price)}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Label className="text-gray-700 text-base">Unit Number *</Label>
                      <p className="text-sm text-amber-400/70 mb-2">Non-exclusive project - enter unit number manually</p>
                      <Input
                        value={manualUnitNumber}
                        onChange={(e) => setManualUnitNumber(e.target.value)}
                        placeholder="Enter unit number (e.g., A-12-05)"
                        className="bg-gray-50 border-gray-100 text-gray-900"
                        data-testid="manual-unit-input"
                      />
                    </div>
                  )}

                  {isExclusive && selectedUnit && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Home className="w-4 h-4 text-[#4E50FF]" />
                        Unit Details (Auto-filled)
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div><span className="text-gray-400">Unit No:</span> <span className="text-gray-900 ml-2">{selectedUnit.unit_number}</span></div>
                        <div><span className="text-gray-400">Floor:</span> <span className="text-gray-900 ml-2">{selectedUnit.floor || '-'}</span></div>
                        <div><span className="text-gray-400">Size:</span> <span className="text-gray-900 ml-2">{selectedUnit.size_sqft || '-'} sqft</span></div>
                        <div><span className="text-gray-400">Price:</span> <span className="text-[#4E50FF] ml-2 font-mono">{formatCurrency(selectedUnit.price)}</span></div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 2: Developer Form Fields */}
          {step === 2 && hasDevTemplate && (
            <div className="space-y-6" data-testid="developer-form-fields">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-medium text-gray-900">{devTemplate?.name}</h3>
                  <Badge className="bg-purple-100 text-purple-700 text-xs">{devTemplate?.developer_name}</Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePreviewOverlay(0)}
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  data-testid="preview-on-pdf-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Preview on PDF
                </Button>
              </div>

              {/* Missing-required-fields banner — always visible on Step 2 so the user
                  sees exactly what's blocking "Next" (complements the toast). */}
              {(() => {
                const missing = missingRequiredFields(2);
                if (missing.length === 0) {
                  return (
                    <div
                      className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm"
                      data-testid="required-fields-ready"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>All required fields are filled. You can proceed to the next step.</span>
                    </div>
                  );
                }
                return (
                  <div
                    className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm"
                    data-testid="required-fields-missing"
                  >
                    <div className="flex items-center gap-2 font-medium mb-1">
                      <AlertCircle className="w-4 h-4" />
                      {missing.length} required field(s) still need to be filled:
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {missing.slice(0, 20).map((label, i) => (
                        <span key={i} className="text-xs bg-white px-2 py-0.5 rounded border border-amber-300 text-amber-800">
                          {label}
                        </span>
                      ))}
                      {missing.length > 20 && (
                        <span className="text-xs text-amber-700">+{missing.length - 20} more</span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {(devTemplateFields?.sections || []).map((section) => (
                <div key={section.name}>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-200">
                    {section.name}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field) => (
                      <div key={field.id} className={['address', 'signature'].includes(field.field_type) ? 'md:col-span-2' : ''}>
                        <Label className="text-gray-700 text-sm flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-400">*</span>}
                          <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">{field.field_type}</Badge>
                        </Label>
                        {field.description && (
                          <p className="text-[11px] text-gray-400 mt-0.5">{field.description}</p>
                        )}
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Fallback Signature Section — only shown when the dev template has NO
                  inline signature fields. Avoids confusing duplicate pads when the
                  template already contains "Signature of First Purchaser" etc. */}
              {(() => {
                const hasInlineSignature = (devTemplateFields?.sections || []).some(s =>
                  (s.fields || []).some(f => f.field_type === 'signature')
                );
                if (hasInlineSignature) return null;
                return (
                  <>
                    <Separator className="bg-gray-200" />
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-2 border-b border-gray-200">
                        Signatures
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SignaturePad
                          label="Buyer / Purchaser Signature *"
                          onSignature={(data) => setSignatures(prev => ({ ...prev, buyer: data }))}
                          existingSignature={signatures.buyer}
                        />
                        <SignaturePad
                          label="Witness / Agent Signature"
                          onSignature={(data) => setSignatures(prev => ({ ...prev, agent: data }))}
                          existingSignature={signatures.agent}
                        />
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Step 3: Payment Details */}
          {step === paymentStep && (
            <div className="space-y-6" data-testid="payment-details-step">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-[#4E50FF]" />
                <h3 className="text-lg font-medium text-gray-900">Booking Fee Payment</h3>
              </div>
              <p className="text-sm text-gray-500 -mt-4">Upload your booking fee payment proof. All fields are mandatory.</p>

              {/* Loan Profiling section removed (Feb 2026) — collected via
                  customer magic-link page. This page is for booking fee tracking only. */}

              {/* Booking Fee Summary */}
              {selectedProperty?.total_booking_fee > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100" data-testid="booking-fee-summary">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Total Required</p>
                      <p className="text-lg font-bold text-gray-900">RM {Number(selectedProperty.total_booking_fee).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">This Payment</p>
                      <p className="text-lg font-bold text-emerald-600">RM {Number(paymentInfo.booking_fee_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Outstanding</p>
                      <p className={`text-lg font-bold ${(selectedProperty.total_booking_fee - (parseFloat(paymentInfo.booking_fee_amount) || 0)) <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        RM {Math.max(0, selectedProperty.total_booking_fee - (parseFloat(paymentInfo.booking_fee_amount) || 0)).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {(selectedProperty.total_booking_fee - (parseFloat(paymentInfo.booking_fee_amount) || 0)) > 0 && (
                    <p className="text-xs text-amber-600 text-center mt-2">Partial payment allowed. Remaining balance can be paid later before SPA signing.</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Booking Fee Amount (RM) *</Label>
                  <Input
                    type="number"
                    min="0"
                    max={selectedProperty?.total_booking_fee || undefined}
                    step="0.01"
                    value={paymentInfo.booking_fee_amount}
                    onChange={(e) => setPaymentInfo({ ...paymentInfo, booking_fee_amount: e.target.value })}
                    className={`bg-gray-50 border-gray-100 text-gray-900 mt-1 ${bookingFeeError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    placeholder="e.g., 10000"
                    data-testid="booking-fee-amount"
                    aria-invalid={!!bookingFeeError}
                    aria-describedby={bookingFeeError ? "booking-fee-error" : undefined}
                  />
                  {bookingFeeError && (
                    <p
                      id="booking-fee-error"
                      data-testid="booking-fee-error"
                      className="text-xs text-red-600 mt-1"
                    >
                      {bookingFeeError}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-700">Payment Method *</Label>
                  <Select value={paymentInfo.payment_method} onValueChange={(v) => setPaymentInfo({ ...paymentInfo, payment_method: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-100 text-gray-900 mt-1" data-testid="payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="credit_card">Credit Card</SelectItem>
                      <SelectItem value="online_banking">Online Banking</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-gray-700">Bank Reference Number *</Label>
                <Input
                  value={paymentInfo.bank_reference}
                  onChange={(e) => setPaymentInfo({ ...paymentInfo, bank_reference: e.target.value })}
                  className="bg-gray-50 border-gray-100 text-gray-900 mt-1"
                  placeholder="e.g., TRX-2026030712345"
                  data-testid="bank-reference"
                />
              </div>

              <Separator className="bg-gray-100" />

              {/* Payment Proof Upload */}
              <div>
                <Label className="text-gray-700 text-base">Payment Proof Upload *</Label>
                <p className="text-sm text-gray-400 mb-3">Upload a screenshot or photo of your payment receipt/slip (max 10MB)</p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePaymentProofChange}
                  accept="image/*,.pdf"
                  className="hidden"
                  data-testid="payment-proof-input"
                />

                {!paymentProofPreview ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-[#4E50FF]/50 hover:bg-[#4E50FF]/5 transition-all"
                    data-testid="payment-proof-dropzone"
                  >
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">Click to upload payment proof</p>
                    <p className="text-gray-400 text-sm mt-1">Supports: JPG, PNG, PDF (max 10MB)</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                          {paymentProofPreview.startsWith('data:image') ? (
                            <img src={paymentProofPreview} alt="Payment proof" className="w-full h-full object-cover" />
                          ) : (
                            <FileText className="w-8 h-8 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 text-sm font-medium truncate">{paymentProofFile?.name}</p>
                          <p className="text-gray-400 text-xs mt-0.5">{(paymentProofFile?.size / 1024).toFixed(1)} KB</p>
                          <Badge className="bg-emerald-500/20 text-[#4E50FF] border-emerald-500/30 text-xs mt-1">Uploaded</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setPaymentProofFile(null); setPaymentProofPreview(null); }}
                          className="text-gray-500 hover:text-red-400"
                          data-testid="remove-payment-proof"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {isConfirmStep && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <CheckCircle className="w-16 h-16 text-[#4E50FF] mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900">Review Your Booking</h2>
                <p className="text-gray-500">Please confirm all details before submitting</p>
              </div>

              {/* Property & Unit */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-[#4E50FF] mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />Property & Unit
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Property:</span> <span className="text-gray-900">{selectedProperty?.name}</span></div>
                  <div><span className="text-gray-400">Location:</span> <span className="text-gray-900">{selectedProperty?.location}</span></div>
                  <div><span className="text-gray-400">Unit:</span> <span className="text-gray-900">{isExclusive ? selectedUnit?.unit_number : manualUnitNumber}</span></div>
                  {isExclusive && selectedUnit?.price && (
                    <div><span className="text-gray-400">Price:</span> <span className="text-[#4E50FF] font-mono">{formatCurrency(selectedUnit?.price)}</span></div>
                  )}
                </div>
              </div>

              {/* Developer Form Data Summary */}
              {hasDevTemplate && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-purple-400 mb-3 flex items-center gap-2">
                    <Wand2 className="w-4 h-4" />{devTemplate?.name} - Form Data
                  </h4>
                  {(devTemplateFields?.sections || []).map((section) => (
                    <div key={section.name} className="mb-3">
                      <h5 className="text-xs font-semibold text-gray-400 uppercase mb-1">{section.name}</h5>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {section.fields.filter(f => formData[f.id]).map((field) => (
                          <div key={field.id}>
                            <span className="text-gray-400">{field.label}:</span>
                            <span className="text-gray-900 ml-2">{formData[field.id]}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-[#4E50FF] mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />Payment Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Booking Fee:</span> <span className="text-[#4E50FF] font-mono ml-2">RM {parseFloat(paymentInfo.booking_fee_amount || 0).toLocaleString()}</span></div>
                  <div><span className="text-gray-400">Payment Method:</span> <span className="text-gray-900 ml-2 capitalize">{paymentInfo.payment_method.replace('_', ' ')}</span></div>
                  <div><span className="text-gray-400">Bank Reference:</span> <span className="text-gray-900 ml-2">{paymentInfo.bank_reference}</span></div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Payment Proof:</span>
                    <Badge className="bg-emerald-500/20 text-[#4E50FF] border-emerald-500/30 text-xs">Uploaded</Badge>
                  </div>
                </div>
              </div>

              {/* Commission Engine V2 — Co-Broke split */}
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4" data-testid="cobroke-section">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="cobroke-toggle"
                    checked={coBrokeEnabled}
                    onCheckedChange={(v) => setCoBrokeEnabled(!!v)}
                    data-testid="cobroke-enable"
                  />
                  <label htmlFor="cobroke-toggle" className="text-sm font-medium text-gray-800 flex items-center gap-1.5 cursor-pointer">
                    <Users2 className="w-4 h-4 text-indigo-600" /> This is a co-broke deal (split commission with other agents)
                  </label>
                </div>
                {coBrokeEnabled && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-gray-500">Up to 4 agents. Each agent's split follows their own leader hierarchy. Allocations must total exactly 100%.</p>
                    {coBrokeRows.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        {r.primary ? (
                          <div className="flex-1 px-3 py-2 rounded-md bg-white border text-sm text-gray-700 truncate">{agentLabel(r.agent_id)}</div>
                        ) : (
                          <Popover open={coBrokePicker === i} onOpenChange={(o) => setCoBrokePicker(o ? i : null)}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" role="combobox" className="flex-1 justify-between font-normal bg-white" data-testid={`cobroke-agent-${i}`}>
                                <span className="truncate">{r.agent_id ? agentLabel(r.agent_id) : <span className="text-gray-400">Select agent</span>}</span>
                                <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                              <Command filter={(value, search) => {
                                const opt = agentsList.find((u) => u.id === value); if (!opt) return 0;
                                const norm = (s) => String(s || '').toLowerCase().replace(/[\s-]/g, '');
                                const hay = norm(`${opt.agent_code || ''} ${opt.user_code || ''} ${opt.full_name || ''} ${opt.email || ''}`);
                                return hay.includes(norm(search)) ? 1 : 0;
                              }}>
                                <CommandInput placeholder="Search agent…" />
                                <CommandList>
                                  <CommandEmpty>No agent found.</CommandEmpty>
                                  <CommandGroup>
                                    {agentsList.filter((u) => u.id !== user?.id).map((u) => (
                                      <CommandItem key={u.id} value={u.id} onSelect={(val) => { setCoBrokeRow(i, { agent_id: val }); setCoBrokePicker(null); }} data-testid={`cobroke-option-${i}-${u.id}`}>
                                        <Check className={`mr-2 h-4 w-4 ${r.agent_id === u.id ? 'opacity-100' : 'opacity-0'}`} />
                                        <span className="text-sm">{u.agent_code || u.user_code} · {u.full_name}</span>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        )}
                        <Input type="number" className="w-24 bg-white" value={r.allocation_percent}
                          onChange={(e) => setCoBrokeRow(i, { allocation_percent: e.target.value })} data-testid={`cobroke-pct-${i}`} placeholder="%" />
                        {!r.primary && (
                          <Button variant="ghost" size="icon" className="text-rose-500" onClick={() => removeCoBrokeRow(i)} data-testid={`cobroke-remove-${i}`}><XIcon className="w-4 h-4" /></Button>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-1">
                      <Button type="button" variant="outline" size="sm" onClick={addCoBrokeRow} disabled={coBrokeRows.length >= 4} data-testid="cobroke-add">+ Add agent</Button>
                      <Badge className={Math.abs(coBrokeTotal - 100) < 0.01 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'} data-testid="cobroke-total">Total {coBrokeTotal}%</Badge>
                    </div>
                  </div>
                )}
              </div>

              {devTemplate && (
                <div className="flex items-center justify-center gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreviewOverlay(0)}
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                    data-testid="preview-filled-pdf-btn"
                  >
                    <Eye className="w-4 h-4 mr-1" />Preview Filled PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadFilledPdf()}
                    disabled={generatingPdf}
                    className="border-emerald-500/50 text-[#4E50FF] hover:bg-emerald-500/10"
                    data-testid="download-filled-pdf-btn"
                  >
                    {generatingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Download Filled PDF
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Overlay Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-400" />
              PDF Preview with Data Overlay
              <Badge className="bg-purple-100 text-purple-700 text-xs ml-2">{devTemplate?.name}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => handlePreviewOverlay(previewPage - 1)} disabled={previewPage === 0 || previewLoading} className="text-gray-700">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-500">Page {previewPage + 1} of {devTemplate?.page_count || 1}</span>
              <Button variant="ghost" size="sm" onClick={() => handlePreviewOverlay(previewPage + 1)} disabled={previewPage >= (devTemplate?.page_count || 1) - 1 || previewLoading} className="text-gray-700">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {previewLoading ? (
              <div className="flex items-center justify-center h-[600px] bg-gray-50 rounded-lg">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Generating preview overlay...</p>
                </div>
              </div>
            ) : previewImage ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <img src={`data:image/jpeg;base64,${previewImage}`} alt="Filled PDF Preview" className="w-full" data-testid="preview-overlay-image" />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[400px] bg-gray-50 rounded-lg text-gray-400">No preview available</div>
            )}
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-200 border border-blue-400"></div>
                <span>Filled field data (highlighted in blue)</span>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => handleDownloadFilledPdf()} disabled={generatingPdf} className="bg-[#4E50FF] hover:bg-[#3436CC]" data-testid="dialog-download-pdf-btn">
                {generatingPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Download Filled PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Navigation */}
      <div className="flex justify-between">
        {step > 1 ? (
          <Button variant="outline" onClick={() => setStep(step - 1)} className="border-gray-200 text-gray-700" data-testid="prev-step">
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
        ) : <div />}

        {!isConfirmStep ? (
          <Button onClick={handleNext} className="bg-[#4E50FF] hover:bg-[#3436CC]" disabled={!canProceed(step)} data-testid="next-step">
            Next<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} className="bg-[#4E50FF] hover:bg-[#3436CC]" disabled={submitting} data-testid="submit-booking">
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : (<><CheckCircle className="w-4 h-4 mr-2" />Create Booking</>)}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreateBooking;
